import { isEqual, pick } from "lodash";
import { Period, PeriodId, getPeriodId } from "@linkfluence/models/date";
import { Timezone } from "@linkfluence/models/timezone";
import { getLastYearPeriod, getPreviousPeriod } from "@tootools/date";
import { getDateFromClause } from "./clause/getter";
import { AIFragment, SearchFragment } from "./search";

type ComparisonPeriod = "last_year" | "previous_period";

function isSecondPeriodIsAPreviousPeriod(
  firstPeriod: Period,
  secondPeriod: Period
) {
  return isEqual(
    getPreviousPeriod(firstPeriod),
    pick(secondPeriod, ["from", "to"])
  );
}

function isSecondPeriodIsALastYearPeriod(
  firstPeriod: Period,
  secondPeriod: Period
) {
  return isEqual(
    getLastYearPeriod(firstPeriod),
    pick(secondPeriod, ["from", "to"])
  );
}

function isSecondPeriodBothPreviousAndLastYearPeriod(
  firstPeriod: Period,
  secondPeriod: Period
) {
  return (
    isSecondPeriodIsAPreviousPeriod(firstPeriod, secondPeriod) &&
    isSecondPeriodIsALastYearPeriod(firstPeriod, secondPeriod)
  );
}

function getComparaisonPeriodFromPeriods(
  firstPeriod: Period,
  secondPeriod: Period
) {
  // Defaults on "previous_period" when both options match the second period
  if (isSecondPeriodBothPreviousAndLastYearPeriod(firstPeriod, secondPeriod)) {
    return "previous_period";
  }
  if (isSecondPeriodIsAPreviousPeriod(firstPeriod, secondPeriod)) {
    return "previous_period";
  }
  if (isSecondPeriodIsALastYearPeriod(firstPeriod, secondPeriod)) {
    return "last_year";
  }
  // Use this default because the semi-fixed range "From a precise date" will create a fixed evolution range
  // e.g. if today is January 20th:
  // - from Jan 15:     Jan 15 ~ now    => today 5 days range, tomorrow 6 days, etc.
  // - previous period: Jan 10 ~ Jan 15 => always 5 days range
  // so after the 1st day, getPreviousPeriod("from Jan 15") != "Jan 10 ~ Jan 15"
  if (getPeriodId(firstPeriod.from, firstPeriod.to) === PeriodId.Custom_From) {
    return "previous_period";
  }

  return null;
}

export function isComparaisonPeriodFromFragmentsBothPreviousAndLastYearPeriod<
  F extends SearchFragment
>(firstFragment: F, secondFragment: F): boolean {
  if (!secondFragment) {
    return false;
  }
  const secondFragmentPeriod = getDateFromClause(secondFragment.request.query);
  const firstFragmentPeriod = getDateFromClause(firstFragment.request.query);
  return isSecondPeriodBothPreviousAndLastYearPeriod(
    firstFragmentPeriod,
    secondFragmentPeriod
  );
}

export function getComparaisonPeriodFromFragments<
  F extends SearchFragment | AIFragment
>(firstFragment: F, secondFragment: F): ComparisonPeriod {
  if (!secondFragment) {
    return null;
  }
  const secondFragmentPeriod = getDateFromClause(secondFragment.request.query);
  const firstFragmentPeriod = getDateFromClause(firstFragment.request.query);

  return getComparaisonPeriodFromPeriods(
    firstFragmentPeriod,
    secondFragmentPeriod
  );
}

export function getTimezoneFromFragment(
  fragment: SearchFragment | AIFragment
): Timezone {
  return fragment?.request.timezone || "UTC";
}

export function hasComparaisonFragment(
  firstFragment: SearchFragment | AIFragment,
  secondFragment: SearchFragment | AIFragment
): boolean {
  if (!secondFragment) {
    return false;
  }
  if (isEqual(firstFragment, secondFragment)) {
    return false;
  }
  return !!getComparaisonPeriodFromFragments(firstFragment, secondFragment);
}
